import { useState, useEffect, useRef } from 'react';
import { createProjectPhase } from "../api/projects";
import { toast } from 'react-toastify';
import { FaChevronDown, FaPlus, FaCog, FaCheck, FaTimes } from 'react-icons/fa';

export const PhaseSelectCreate = ({
  onChange,
  phases,
  defaultValue,
  onCreate,
  token,
  project_id,
}: {
  onChange: (value: any) => void;
  onCreate: (value: any) => void;
  phases: ProjectPhaseInterface[];
  defaultValue: string;
  token: string | undefined;
  project_id: string;
}) => {
  const [isPhaseLoading, setIsPhaseLoading] = useState(false);
  const [phaseOptions, setPhaseOptions] = useState<any[]>(phases?.map((p) => ({ label: p.name, value: p.id })) || []);
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPhaseName, setNewPhaseName] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update phaseOptions when phases prop changes
  useEffect(() => {
    setPhaseOptions(phases?.map((p) => ({ label: p.name, value: p.id })) || []);
  }, [phases]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowCreateForm(false);
        setNewPhaseName('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleCreatePhase = async (inputValue: string) => {
    if (!inputValue.trim()) return;
    
    setIsPhaseLoading(true);
    const id = crypto.randomUUID();
    try {
      const res = await createProjectPhase(token || '', project_id, {
        id,
        project_id: project_id,
        name: inputValue.trim(),
        status: 'NOT_STARTED',
      });

      // Immediately add the new phase to local options
      const newPhaseOption = { label: inputValue.trim(), value: res.id };
      setPhaseOptions(prev => [...prev, newPhaseOption]);

      onCreate(res.id);
      setNewPhaseName('');
      setShowCreateForm(false);
      setIsOpen(false);
      toast.success('Phase created successfully');
    } catch (error) {
      toast.error('Failed to create phase');
    }
    setIsPhaseLoading(false);
  };

  const selectedPhase = phaseOptions.find(option => option.value === defaultValue);

  return (
    <div className="phase-select-wrapper flex items-center gap-2">
      {/* Phase Selector */}
      <div className="relative flex-1" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between px-3 py-2 bg-white border border-gray-300 rounded-md hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-indigo-600 transition-colors"
        >
          <span className="text-sm font-medium text-gray-700">
            {selectedPhase ? selectedPhase.label : 'Select phase...'}
          </span>
          <FaChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {/* Existing Phases */}
            {phaseOptions.length > 0 ? (
              <div className="py-1">
                {phaseOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setIsOpen(false);
                      onChange(option);
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 flex items-center justify-between ${
                      option.value === defaultValue ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <span className="text-gray-900">{option.label}</span>
                    {option.value === defaultValue && (
                      <FaCheck className="w-3 h-3 text-indigo-600" />
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                No phases available
              </div>
            )}

            {/* Divider */}
            <div className="border-t border-gray-200"></div>

            {/* Create New Phase */}
            <div className="p-2 bg-gray-50">
              {!showCreateForm ? (
                <button
                  type="button"
                  onClick={() => setShowCreateForm(true)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                >
                  <FaPlus className="w-3 h-3" />
                  <span>Create new phase</span>
                </button>
              ) : (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={newPhaseName}
                    onChange={(e) => setNewPhaseName(e.target.value)}
                    placeholder="Enter phase name..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-600"
                    autoFocus
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleCreatePhase(newPhaseName);
                      } else if (e.key === 'Escape') {
                        setShowCreateForm(false);
                        setNewPhaseName('');
                      }
                    }}
                  />
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleCreatePhase(newPhaseName)}
                      disabled={!newPhaseName.trim() || isPhaseLoading}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      {isPhaseLoading ? (
                        <>
                          <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Creating...</span>
                        </>
                      ) : (
                        <>
                          <FaCheck className="w-3 h-3" />
                          <span>Create</span>
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateForm(false);
                        setNewPhaseName('');
                      }}
                      className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      <FaTimes className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Manage Phases Button */}
      <button
        type="button"
        onClick={() => {
          // This could open a full phase management modal
          // For now, we'll just show a tooltip-like message
          toast.info('Use the dropdown to create new phases, or ask your admin to manage phases');
        }}
        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 hover:text-gray-700 transition-colors"
        title="Manage Phases"
      >
        <FaCog className="w-3 h-3" />
        <span className="hidden sm:inline">Manage</span>
      </button>
    </div>
  );
};